import { Result, Button } from 'antd'
import { DownloadOutlined } from '@ant-design/icons'
import { useSocketRequest } from 'shared/hooks/useSocketRequest'
import { IMAGES_URL } from 'shared/constants/urls'
import { productApi } from 'api/product'
import { memo, useCallback, useEffect, useState } from 'react'
import { ThreeDot } from 'react-loading-indicators'
import { EEnhancementImageType, EnhancementImageResponse } from 'models/product'
import { VariantButton } from 'components/ui/VariantButton/VariantButton'
import { RegenerateAiButton } from 'components/ui/RegenerateAiButton/RegenerateAiButton'
import { showMessage } from 'shared/utils/showMessage'
import { OrangeButton } from 'components/ui/OrangeButton/OrangeButton'
import { AiEnhancementModal } from '../AiEnhancementModal/AiEnhancementModal'
import styles from './AiProductImage.module.scss'

interface AiProductImageProps {
  productId: string
  initialImages?: EnhancementImageResponse[]
  newImages?: EnhancementImageResponse[]
  selectedImage: EnhancementImageResponse | null
  setSelectedImage: (image: EnhancementImageResponse) => void
  setItemId: (id: string) => void
  type: 'create' | 'edit',
  generateType?: EEnhancementImageType
  additionalPrompt?: string
}

export const AiProductImage: React.FC<AiProductImageProps> = memo(({ productId, initialImages = [], newImages = [], type, generateType, additionalPrompt, selectedImage, setSelectedImage, setItemId }) => {
  const [images, setImages] = useState<EnhancementImageResponse[]>(initialImages)
  const [isDownloading, setIsDownloading] = useState(false)

  const [generateImageType, setGenerateImageType] = useState<EEnhancementImageType>(generateType || EEnhancementImageType.FULL)
  const [isRegenerateImageModalOpen, setIsRegenerateImageModalOpen] = useState(false)

  const createJob = useCallback(async () => {
    const { data } = await productApi.createAiImage({
      productId,
      type: generateImageType,
      additionalPrompt
    })
    return { jobId: data.jobId }
  }, [productId, generateImageType, additionalPrompt])

  const { isLoaded, isError, data, retry } = useSocketRequest<EnhancementImageResponse>({
    enabled: type === 'create' && !newImages?.length,
    createJob
  })

  const downloadImage = useCallback(async () => {
    if (!selectedImage) {
      showMessage('error', 'Нет выбранного изображения для скачивания', '')
      return
    }

    setIsDownloading(true)
    try {
      const imageUrl = `${IMAGES_URL}${selectedImage.image}`
      const response = await fetch(imageUrl)

      if (!response.ok) {
        throw new Error('Ошибка при загрузке изображения')
      }

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)

      // Определяем расширение файла
      let fileExtension = '.jpg' // fallback

      // Пытаемся извлечь расширение из URL
      const urlPath = selectedImage.image
      const urlExtension = urlPath.match(/\.(jpg|jpeg|png|webp|gif|bmp|tiff)$/i)
      if (urlExtension) {
        fileExtension = urlExtension[0].toLowerCase()
      } else {
        // Если не получилось из URL, определяем по Content-Type
        const contentType = response.headers.get('content-type')
        if (contentType) {
          if (contentType.includes('png')) fileExtension = '.png'
          else if (contentType.includes('jpeg')) fileExtension = '.jpeg'
          else if (contentType.includes('jpg')) fileExtension = '.jpg'
          else if (contentType.includes('webp')) fileExtension = '.webp'
          else if (contentType.includes('gif')) fileExtension = '.gif'
          else if (contentType.includes('bmp')) fileExtension = '.bmp'
          else if (contentType.includes('tiff')) fileExtension = '.tiff'
        }
      }

      // Создаем временную ссылку для скачивания
      const link = document.createElement('a')
      link.href = url
      link.download = `product-image-${selectedImage._id}${fileExtension}`
      document.body.appendChild(link)
      link.click()

      // Очищаем ресурсы
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch {
      showMessage('error', 'Ошибка при скачивании изображения', '')
    } finally {
      setIsDownloading(false)
    }
  }, [selectedImage])

  const handleUpdateImagesList = (type: EEnhancementImageType, images: EnhancementImageResponse[] | null, selectedImage: EnhancementImageResponse | null) => {
    if (type === EEnhancementImageType.BACKGROUND) {
      setImages(prev => [...prev, ...(images as EnhancementImageResponse[])])
      setSelectedImage(selectedImage as EnhancementImageResponse)
    } else {
      setGenerateImageType(type)
      setTimeout(() => {
        retry()
      }, 500)
    }
  }

  useEffect(() => {
    if (data) {
      setImages([...images, data])
      setSelectedImage(data)
      setItemId(data.enhancementId)
    }
  }, [data])

  useEffect(() => {
    if (!!newImages?.length) {
      setImages(prev => [...prev, ...newImages])
    }
  }, [newImages])


  return (
    <>
      <div className={styles.container}>
        {!isLoaded && (
          <ThreeDot color='#FF7905' size='medium' text='' textColor='' />
        )}
        {isError && isLoaded && (
          <Result
            status='error'
            title='Ошибка при генерации изображения'
            subTitle='Попробуйте еще раз'
            extra={[
              <OrangeButton onClick={retry}>
                Попробовать еще раз
              </OrangeButton>
            ]}
          />
        )}
        {!isError && isLoaded && selectedImage && (
          <div className={styles.content}>
            <div className={styles.imageWrapper}>
              <img src={`${IMAGES_URL}${selectedImage.image}`} alt='product' />
            </div>
            <div className={styles.buttonsRow} style={{ marginTop: 'auto' }}>
              {images.map((image, index) => (
                <VariantButton
                  text={`Вариант ${index + 1}`}
                  isActive={selectedImage?.image === image.image}
                  onClick={() => setSelectedImage(image)}
                  key={image.image}
                />
              ))}
            </div>
            <div className={styles.buttonsRow}>
              <RegenerateAiButton size='large' onClick={() => setIsRegenerateImageModalOpen(true)} />
              <Button
                type='default'
                size='large'
                icon={<DownloadOutlined />}
                loading={isDownloading}
                onClick={downloadImage}
                className={`${styles.downloadImage} ${styles.active}`}
              >
                {isDownloading ? 'Скачивание...' : 'Скачать'}
              </Button>
            </div>
          </div>
        )}
      </div>
      <AiEnhancementModal
        type='edit'
        scope='image'
        productId={productId}
        visible={isRegenerateImageModalOpen}
        onClose={() => setIsRegenerateImageModalOpen(false)}
        updateImagesList={handleUpdateImagesList}
      />
    </>
  )
})
